import { HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FinancialTitleRepository } from '@asm/features/sync/repositories/financial-title.repository';
import { ASM_UNIT_ID_KEY } from '@shared/tools/constants';

/**
 * Handler offline para títulos financeiros.
 *
 * Cobre:
 * - GET /financial-title/has-overdue
 *
 * O b2bUnitUid é resolvido em cascata:
 *   1. Header `x-b2bunit-id` enviado pela requisição
 *   2. Fallback: isolated ASM unit in localStorage
 */
@Injectable({ providedIn: 'root' })
export class FinancialTitleOfflineHandler {
  private readonly financialTitleRepo = inject(FinancialTitleRepository);

  canHandle(req: HttpRequest<unknown>): boolean {
    return req.method === 'GET' && req.url.includes('/financial-title/has-overdue');
  }

  async handle(req: HttpRequest<unknown>): Promise<HttpResponse<unknown>> {
    // Resolve b2bUnitUid: header da requisição → fallback localStorage
    const fromHeader = String(req.headers.get('x-b2bunit-id') ?? '').trim();
    const b2bUnitUid = fromHeader || String(localStorage.getItem(ASM_UNIT_ID_KEY) ?? '').trim();
    return new HttpResponse({ status: 200, body: await this.hasOverdueTitles(b2bUnitUid) });
  }

  /**
   * Verifica se a unidade B2B possui títulos financeiros vencidos e não pagos.
   *
   * Um título é considerado vencido quando:
   * - Pertence à unidade (`b2bUnitUid` coincide)
   * - Não tem data de pagamento registrada (`paymentDate` vazio)
   * - A data de vencimento (`dueDate`) é anterior à data atual
   */
  private async hasOverdueTitles(b2bUnitUid: string): Promise<boolean> {
    if (!b2bUnitUid) return false;

    const titles = await firstValueFrom(this.financialTitleRepo.getAll());
    const now = new Date();

    return titles.some(title => {
      if (String(title.b2bUnitUid ?? '').trim() !== b2bUnitUid) return false;
      if (String(title.paymentDate ?? '').trim()) return false;

      const dueDateRaw = String(title.dueDate ?? '').trim();
      if (!dueDateRaw) return false;

      const dueDate = new Date(dueDateRaw);
      return !Number.isNaN(dueDate.getTime()) && dueDate < now;
    });
  }
}
